import type { FocusableElement } from '@react-types/shared';

import { forwardRef, useMemo } from 'react';

import type { PriceType } from '../../models/priceData/priceData';
import type { ProductVariantProps } from '../../models/props/itemTeaserProps';
import type { ProductSearchListingItemData } from './ProductSearchListingItem.type';

import { ProductListingItemNew as ProductListingItem } from '../../../cart/components/molecules/ProductListingItem';
import { EdlpPriceBadge } from '../../../ecommerce/components/EdlpPricingSplash';
import { useIsUserAMember } from '../../hooks/useUserProfile';
import { useIsB2BRoute } from '../../utils/b2bUtils';
import { AddToCartButton } from '../AddToCartButton';
import VariantSelector from '../atoms/VariantSelector';
import styles from './ProductSearchListingItem.module.scss';
import { getProductSearchListingItemA11yText } from './ProductSearchListingItem.utils';
import ProductSearchListingItemPriceLabels from './ProductSearchListingItemPriceLabels';

interface ProductSearchListingItemProps {
    handleLinkClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    handleVariantChange?: (variantId: string) => void;
    isALink: boolean;
    isAsyncLink: boolean;
    currentVariant?: ProductVariantProps;

    product: ProductSearchListingItemData;
    quantity: number;
    onQuantityUpdate: (newQuanity: number) => void;
    priceType: PriceType;
    onBuyButtonClick?: (e: React.MouseEvent<FocusableElement>) => void;
    onAddToCartClick?: (e: React.MouseEvent<HTMLElement>) => void;
    onRemoveFromCartClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

// TODO rename this whole component, it has nothing to do with search
const ProductSearchListingItem = forwardRef<HTMLDivElement, ProductSearchListingItemProps>(
    (props, ref) => {
        const a11yTextType = props.isALink ? 'ariaHidden' : 'visuallyHiddenText';

        const isB2B = useIsB2BRoute();
        const isUserMember = useIsUserAMember();

        const a11yLabel = useMemo(
            () =>
                getProductSearchListingItemA11yText({
                    product: props.product,
                    variant: props.currentVariant,
                    priceType: props.priceType,
                    a11yTextType,
                    isB2B,
                    isUserMember,
                }),
            [
                props.product,
                props.currentVariant,
                props.priceType,
                a11yTextType,
                isB2B,
                isUserMember,
            ],
        );

        return (
            <ProductListingItem.Container
                ref={ref}
                isALink={props.isALink}
                className={styles.Container}
                product={props.product}
                a11yLabel={a11yLabel}
                a11yType={a11yTextType}
                handleLinkClick={props.handleLinkClick}
                leadingBadgeSlot={<EdlpPriceBadge promotion={props.product.promotion} />}
            >
                {!!props.product.variants?.length &&
                    props.currentVariant &&
                    props.handleVariantChange && (
                        <ProductListingItem.VariantSelector>
                            <VariantSelector
                                variants={props.product.variants}
                                selectedVariantId={props.currentVariant?.id}
                                handleVariantChange={props.handleVariantChange}
                            />
                        </ProductListingItem.VariantSelector>
                    )}
                <div className={styles.Bottom}>
                    <ProductListingItem.PricingContainer>
                        <ProductListingItem.PrimaryPricing>
                            <ProductSearchListingItemPriceLabels
                                type="primary"
                                product={props.product}
                                variant={props.currentVariant}
                                priceType={props.priceType}
                                a11yTextType={a11yTextType}
                                isB2B={isB2B}
                                isUserMember={isUserMember}
                            />
                        </ProductListingItem.PrimaryPricing>
                        <ProductListingItem.SecondaryPricing>
                            <ProductSearchListingItemPriceLabels
                                type="secondary"
                                product={props.product}
                                variant={props.currentVariant}
                                priceType={props.priceType}
                                a11yTextType={a11yTextType}
                                isB2B={isB2B}
                                isUserMember={isUserMember}
                            />
                        </ProductListingItem.SecondaryPricing>
                    </ProductListingItem.PricingContainer>
                    <AddToCartButton
                        className="u-flexShrinkNone"
                        size="large"
                        withButton
                        quantity={props.quantity}
                        buyButtonText="Lägg i varukorg"
                        getBuyButtonLabel={(name) => `Lägg i varukorg, ${name}`}
                        setQuantity={props.onQuantityUpdate}
                        productName={props.product.name}
                        onAddToCartClick={props.onAddToCartClick}
                        onBuyButtonClick={props.onBuyButtonClick}
                        onRemoveFromCartClick={props.onRemoveFromCartClick}
                        autoFocus
                    />
                </div>
            </ProductListingItem.Container>
        );
    },
);

export default ProductSearchListingItem;
